// InfoFeed.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button
} from "reactstrap";
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import useInfoFeed from '../../../hooks/useInfoFeed';

import { Link, useNavigate } from "react-router-dom";

import { UserSearch } from 'lucide-react';

import { fetchImage } from "utils/images/imageManager";
import placeholderSmall from "assets/images/info/card-placeholder-small.png";

import GalleryModal from './components/GalleryModal';

import axiosInstance from 'utils/axiosInstance';

import './InfoFeed.css';

const PostCard = ({ post, liked, setLikedMap, imageMap, navigate }) => {
  const [localLikeCount, setLocalLikeCount] = useState(0);
  const [isLiked, setIsLiked] = useState(liked);

  // NEW: gallery controls
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // resolve full-size image srcs (from your imageMap / placeholder)
  const imageSrcs = useMemo(() => {
    return (post.images ?? []).map((name) => imageMap.get(name) || placeholderSmall);
  }, [post.images, imageMap]);

  useEffect(() => {
    setIsLiked(liked);
  }, [liked]);

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });

  useEffect(() => {
    if (inView) {
      axiosInstance.post('/knovia/myInfo/infoEvents', { postId: post._id, type: 'view' })
        .catch(console.error);
    }
  }, [inView]);

  
  const handleLikeToggle = async () => {
    try {
      if (isLiked) {
        await axiosInstance.delete('/knovia/myInfo/infoEvents', {
          data: { postId: post._id, type: 'like' }
        });
        setLocalLikeCount(c => c - 1);
        setLikedMap(prev => ({ ...prev, [post._id]: false }));
      } else {
        await axiosInstance.post('/knovia/myInfo/infoEvents', { postId: post._id, type: 'like' });
        setLocalLikeCount(c => c + 1);
        setLikedMap(prev => ({ ...prev, [post._id]: true }));
      }
      setIsLiked(!isLiked);
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  return (
    <div ref={ref} className="mb-4 pb-4 border-bottom">
      {post.topics?.length > 0 && (
        <div className="mb-2">
          {post.topics.map((topic, i) => (
            <span
              key={i}
              className="badge bg-secondary me-1"
              style={{ fontSize: '0.75rem' }}
            >
              #{topic}
            </span>
          ))}
        </div>
      )}

      <div style={{ margin: "10px 0px 8px 0px" }} className="d-flex justify-content-between">
        <div className="d-flex">
          <img src={imageMap.get(post.profileImage) || placeholderSmall} alt="preview" 
            style={{
              height: "40px",
              width: "40px",
              objectFit: "cover",
              borderRadius: "50%",
              border: "1px solid #6c97d1",
              marginRight: "10px"
            }} 
          />
          <div style={{ marginTop: "2.7px", marginRight: "10px" }}>
            <p className="mb-0">{post?.questionSetLabel}</p>
            <p
              className="mb-0"
              style={{
                fontSize: "10.5px",
                color: "#9b9b9b",
              }}
            >
              <Link
                to={`/myInfo/storeMenu/stores/${post?.user?.myInfo?._id}`} 
                style={{ color: "#9b9b9b", textDecoration: "none" }}
              >
                {post.user.myInfo.displayName}
              </Link>
              {" ・ "}
              <Link
                to={`/myInfo/storeMenu/topics/${post?.sourceQuestionSet?.topic?._id}`} 
                style={{ color: "#9b9b9b", textDecoration: "none" }}
              >
                {post?.topicLabel}
              </Link>
            </p>
          </div>
        </div>
        {post?.pointReward?.hasReward && 
          <div className="d-flex justify-content-end align-items-start" 
            style={{ paddingTop: "5px", paddingRight: "10px", minWidth: "80px" }}
          >
            <div className="info-feed-post-point-btn"
              style={{
                padding: "2px 5px",
                border: "1px solid #4e99e1",
                borderRadius: "3px"
              }}
            >
              <p className="mb-0 info-feed-post-point-text" 
                style={{ fontWeight: 400, fontSize: "9.5px" }}>
                {`TTpoint ${post?.pointReward?.amount || 0}`}
              </p>
            </div>
          </div>
        }
      </div>

      <div 
        style={{ cursor: "pointer" }}
        onClick={()=>{navigate(`/myInfo/questionSets/${post.sourceQuestionSet}`);}}
      >
        <p style={{ marginTop: "15px", marginBottom: "12px" }}>
          {post.format === 'md'
            ? <ReactMarkdown>{post.text}</ReactMarkdown>
            : <p>{post.text}</p>}
        </p>
      </div>

      <div className="scroll-container-no-scrollbar"
        style={{ overflowX: "auto", whiteSpace: "nowrap", paddingBottom: "5px",
          marginBottom: "6px", scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div className="d-flex align-items-center" style={{ width: "max-content" }}>
          {post.images?.length > 0 && post.images?.map((imageName, idx) => (
            <div 
              key={idx} 
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => {
                setGalleryStartIndex(idx);
                setIsGalleryOpen(true);
              }}
             >
              <img
                src={imageMap.get(imageName) || placeholderSmall}
                alt={`preview-${idx}`}
                style={{
                  height: "150px",
                  objectFit: "cover",
                  maxWidth: "180px",
                  borderRadius: "10px",
                  border: "0.5px solid #959595",
                  marginRight: "10px"
                }}
              />
            </div>
          ))}

        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <span className="text-muted small">
          {new Date(post.createdAt).toLocaleString()}
        </span>
        <div className="d-flex align-items-center">
          <span className="d-flex me-3 text-muted">
            <UserSearch className="me-1" size={16} /> 
            <p className="mb-0">{post.views ?? 0}</p>
          </span>
          <Button 
            color={isLiked ? 'danger' : 'outline-danger'}
            size="sm" 
            onClick={handleLikeToggle}
          >
            ❤️ {(post.likes ?? 0) + localLikeCount}
          </Button>
        </div>
      </div>

      {/* NEW: Lightbox for this post */}
      <GalleryModal
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        images={imageSrcs}
        startIndex={galleryStartIndex}
        title={post?.questionSetLabel || ''}
      />
    </div>
  );
}

const InfoFeed = () => {

  const navigate = useNavigate();

  const [likedMap, setLikedMap] = useState({});
  const [imageMap, setImageMap] = useState(new Map());

  const availableTopics = ['tech', 'music', 'gaming', 'design', 'news'];
  const [selectedTopics, setSelectedTopics] = useState([]);

  const [postFilter, setPostFilter] = useState("all");
  const {
    data,
    fetchNextPage,
    hasNextPage,
    status,
    isFetchingNextPage,
    isFetching,
  } = useInfoFeed(selectedTopics, postFilter);
  const { ref, inView } = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, hasNextPage]);

  const posts = useMemo(() => {
    return data?.pages.flatMap(p => p.items) ?? [];
  }, [data]);

  useEffect(()=>{
    console.log('posts:', posts);
  },[posts])
  


  const createdObjectUrls = new Set(); // track urls
  useEffect(() => {
    const loadImages = async () => {
      const newMap = new Map(imageMap); // preserve existing

      const uniqueImageNames = new Set();
      for (const post of posts) {
        // post content images
        for (const imageName of post.images ?? []) {
          if (imageName && !newMap.has(imageName)) {
            uniqueImageNames.add(imageName);
          }
        }

        // profile image
        const profileImageName = post.profileImage;
        if (profileImageName && !newMap.has(profileImageName)) {
          uniqueImageNames.add(profileImageName);
        }
      }

      await Promise.all(
        Array.from(uniqueImageNames).map(async (imageName) => {
          try {
            const result = await fetchImage(imageName, "original");
            if (result?.blob) {
              const objectUrl = URL.createObjectURL(result.blob);
              createdObjectUrls.add(objectUrl); // track it
              newMap.set(imageName, objectUrl);
            }
          } catch (e) {
            console.error("Failed to load image:", imageName);
          }
        })
      );

      setImageMap(newMap);
    };

    loadImages();
  }, [posts]); // rerun when posts change

  useEffect(() => {
    return () => {
      for (const url of createdObjectUrls) {
        URL.revokeObjectURL(url);
      }
      createdObjectUrls.clear(); // clean the tracker
    };
  }, []);

  useEffect(() => {
    
    const fetchLikedMap = async () => {
      const postIds = posts.map(p => p._id).join(',');
      if (!postIds) return;
      try {
        const likedResponse = await axiosInstance.post('/knovia/myInfo/infoEvents/likedInfoPosts', 
          { ids: postIds }
        );
        console.log('likedResponse.data:', likedResponse.data);
        const map = {};
        likedResponse.data.likedPostIds.forEach(id => { map[id] = true });
        setLikedMap(map);
      } catch (err) {
        console.error('Failed to fetch liked posts', err);
      }
    }
   
    console.log("Post IDs loaded:", posts.map(p => p._id));
    fetchLikedMap();
  }, [posts]);


  //----------------------------------
  // Scroll-aware top bar state
  //----------------------------------

  const [showTopBar, setShowTopBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const threshold = 20;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (Math.abs(currentScrollY - lastScrollY) < threshold) {
        // Not enough movement to toggle
        return;
      }

      if (currentScrollY > lastScrollY) {
        // scrolling down → hide
        setShowTopBar(false);
      } else {
        // scrolling up → show
        setShowTopBar(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <React.Fragment>

      {/* Top bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          width: "100%",
          height: "60px",
          backgroundColor: "white",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          transition: "transform 0.5s ease-in-out",
          transform: showTopBar ? "translateY(0)" : "translateY(-100%)",
          zIndex: 1000,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
        }}
      >
        <div>
          <h5 style={{ margin: 0 }}>貼文動態牆</h5>
          {/*
          <div className="mt-1 mb-0" style={{ minHeight: "10px", fontSize: "8px" }}>
            {isFetching && !isFetchingNextPage ? (
              <p className="mb-0 text-muted">正在載入題組...</p>
            ) : (
              <p className="mb-0 text-muted">題組加載完成</p>
            )}
          </div>
          */}
        </div>
        {/* Right: Filter Buttons */}
        <div className="d-flex">
          <div 
            className={`me-2 post-filter-button ${postFilter === "all" ? 'active' : ''}`}
            onClick={() => setPostFilter("all")}
          >
            <p className="mb-0">全部</p>
          </div>
          <div
            className={`me-2 post-filter-button ${postFilter === "following" ? 'active' : ''}`}
            onClick={() => setPostFilter("following")}
          >
            <p className="mb-0">追蹤中</p>
          </div>    
        </div>
      </div>

      <div className="page-content info-feed">
        <Container fluid className="info-feed-container">
          <Card className="info-feed-card" style={{ paddingTop: '60px' }}>
            <CardBody className="info-feed-card-body">

              {/*
              <div>
                {availableTopics.map(topic => (
                  <Button
                    key={topic}
                    disabled={status === 'loading'}
                    color={selectedTopics.includes(topic) ? 'primary' : 'outline-primary'}
                    size="sm"
                    className="me-2 mb-2"
                    onClick={() => {
                      setSelectedTopics(prev =>
                        prev.includes(topic)
                          ? prev.filter(t => t !== topic)
                          : [...prev, topic]
                      );
                    }}
                  >
                    #{topic}
                  </Button>
                ))}
              </div>
              */}

              {posts.map(p => (
                <PostCard 
                  key={p._id} 
                  post={p} 
                  liked={!!likedMap[p._id]} 
                  setLikedMap={setLikedMap} 
                  imageMap={imageMap}
                  navigate={navigate}
                />
              ))}

              <div ref={ref} className="py-6 text-center">
                {isFetchingNextPage
                  ? '正在加載更多題組…'
                  : !hasNextPage
                    ? '— 沒有更多題組囉 —'
                    : null}
              </div>

            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default InfoFeed;



